import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, Warning, User, CalendarBlank, Eye, EyeSlash } from "@phosphor-icons/react";
import PdfPreview from "@/components/PdfPreview";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function RechazoDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rejection-records/${id}`);
        setR(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/rejection-records/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error("[RechazoDetail] pdf HTTP", res.status, errText);
        toast.error(`No se pudo generar el PDF (${res.status})`);
        return;
      }
      const blob = await res.blob();
      if (blob.type !== "application/pdf") {
        // Backend returned an error page instead of the PDF
        const errText = await blob.text();
        console.error("[RechazoDetail] wrong content-type:", blob.type, errText);
        toast.error("Respuesta inválida del servidor");
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rechazo_${r?.folio || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.debug("[RechazoDetail] pdf failed", err);
      toast.error("No se pudo descargar el PDF");
    }
  };

  if (loading) return <div className="p-8 text-white/50 text-sm">Cargando…</div>;
  if (!r) return <div className="p-8 text-white/50 text-sm">No encontrado.</div>;

  const totalPz = (r.products || []).reduce((s, p) => s + (Number(p.pieces) || 0), 0);

  return (
    <div>
      <Link to="/rechazos" className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white mb-4">
        <ArrowLeft size={14} /> Volver a rechazos
      </Link>

      <div className="bg-[#111111] border border-white/10 rounded-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-1">
              Rechazo por Producto Maltratado
            </div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <h1 className="font-mono-data font-bold text-5xl text-[#FF3B30]" data-testid="rch-detail-folio">
                {r.folio}
              </h1>
              <div className="text-white/60 text-sm flex items-center gap-1">
                <CalendarBlank size={14} /> {fmt(r.elaboration_date)}
              </div>
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-6 text-sm">
              <Meta label="Proveedor" value={r.provider_name} />
              <Meta label="Cita RCN" value={r.appointment_folio || "—"} />
              <Meta label="Elaborado por" value={r.elaborated_by} icon={User} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowPreview((v) => !v)}
              data-testid="rch-toggle-preview"
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/15 bg-white/5 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
            >
              {showPreview ? <EyeSlash size={16} /> : <Eye size={16} weight="fill" />}
              {showPreview ? "Ocultar vista previa" : "Vista previa"}
            </button>
            <button
              onClick={downloadPdf}
              data-testid="rch-download-pdf"
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm font-semibold hover:bg-[#FF3B30]/20 transition-colors"
            >
              <FilePdf size={16} weight="fill" /> Descargar PDF
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview (toggle) */}
      {showPreview && (
        <div className="mb-6">
          <PdfPreview endpoint={`/rejection-records/${id}/pdf`} testId="rch-pdf-preview" />
        </div>
      )}

      {/* Causas */}
      <Card title="Causas del rechazo">
        <div className="flex flex-wrap gap-2">
          {(r.causes || []).map((c) => (
            <span key={c} className="px-3 py-1.5 rounded-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-xs">
              {c}
            </span>
          ))}
          {r.custom_cause && (
            <span className="px-3 py-1.5 rounded-sm bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-xs">
              Otros: {r.custom_cause}
            </span>
          )}
          {(r.causes || []).length === 0 && !r.custom_cause && (
            <span className="text-white/40 text-sm">Sin causas registradas.</span>
          )}
        </div>
      </Card>

      {/* Productos */}
      <Card title={`Productos afectados · ${totalPz} PZ totales`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40">
              <tr className="border-b border-white/10">
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">VPN</th>
                <th className="text-left px-3 py-2">Series</th>
                <th className="text-right px-3 py-2">PZ</th>
              </tr>
            </thead>
            <tbody>
              {(r.products || []).length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-white/40">Sin productos capturados.</td></tr>
              ) : (
                r.products.map((p, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="px-3 py-2 font-mono-data text-white/40">{i + 1}</td>
                    <td className="px-3 py-2 font-mono-data text-white">{p.sku || "—"}</td>
                    <td className="px-3 py-2 text-white/70">{p.vpn || "—"}</td>
                    <td className="px-3 py-2 text-white/70">{p.series || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data text-[#F59E0B] font-bold">{p.pieces || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Comentarios */}
      <Card title="Comentarios generales">
        <div className="text-sm text-white/80 whitespace-pre-wrap">{r.comments || "—"}</div>
      </Card>

      {/* Firmas */}
      <Card title="Firmas">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SignatureBox title="ENTREGA el rechazo" name={r.deliverer_name} sig={r.deliverer_signature} />
          <SignatureBox title="RECIBE el rechazo" name={r.receiver_name} sig={r.receiver_signature} />
        </div>
      </Card>
    </div>
  );
}

function Meta({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/40 mb-1 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="text-white">{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm mb-6">
      <div className="px-5 py-3 border-b border-white/10">
        <div className="font-mono-data text-[11px] uppercase tracking-widest text-white/80">{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SignatureBox({ title, name, sig }) {
  return (
    <div>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/40 mb-2">{title}</div>
      <div className="bg-white rounded-sm border border-white/10 flex items-center justify-center h-32">
        {sig ? (
          <img src={sig} alt="firma" className="max-h-full max-w-full" />
        ) : (
          <span className="text-black/40 text-xs">Sin firma capturada</span>
        )}
      </div>
      <div className="mt-2 pt-2 border-t border-white/10 text-white font-medium">
        {name || <span className="text-white/40">Sin nombre</span>}
      </div>
    </div>
  );
}
